/**
 * Single source of truth for whether the home AI-search experience is enabled for
 * the current session. Derived entirely from cookies (`userid`, `usermisc`,
 * `AIModeActive`) so it can be evaluated identically on the server (SSR) and the
 * client (hydration) — mirroring the `getIsInternalUser` server-twin pattern.
 */

/** Cookie flag that force-enables AI mode for a session (set during rollout / QA). */
export const AI_MODE_ACTIVE_COOKIE = 'AIModeActive'

/** Internal users auto-opted into the home AI-search experience. */
const AI_ACCESS_EMAILS = [
    'jay.prakash@flyin.com',
    'pradip.mandal@flyin.com',
    'himanshu@flyin.com',
    'rameshwar.patil@flyin.com',
    'jairam.nagasaivc@flyin.com',
    'prasad.bandaruvc@flyin.com',
    'aryan.kaushik@flyin.com',
    'nischay.prasher@flyin.com',
    'sourabh@flyin.com',
    'vishwa@flyin.com',
    'nakul.goyal@flyin.com',
    'manoj@flyin.com'
].map(email => email.toLowerCase())

/** Reads a cookie's raw value by name. Backed by `document.cookie` on the client and `cookies()` on the server. */
export type CookieReader = (name: string) => string | null | undefined

const decode = (value?: string | null): string => {
    if (!value) return ''
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

/**
 * Computes home AI-search enablement from cookies.
 */
export const computeHomeAiEnabled = (getCookie: CookieReader): boolean => {
    // `userid` cookie layout matches getUserAuthValues(): email|profileName|gender|photo|userId
    const [email = '', , , , userId = ''] = decode(getCookie('userid')).split('|')
    const isSignedIn = decode(getCookie('usermisc')).split('|').includes('SIGNED_IN') && userId.length > 0
    const hasWhitelistAccess =
        isSignedIn && !!email && AI_ACCESS_EMAILS.includes(email.toLowerCase().trim())
    const isActiveFromCookie = getCookie(AI_MODE_ACTIVE_COOKIE) === 'true'
    return hasWhitelistAccess || isActiveFromCookie
}
